import type { ReactNode, ButtonHTMLAttributes, InputHTMLAttributes } from "react";

// The gilded kit, matching the apps' components (PrimaryButton / QuietButton /
// GildedFrame / ScreenHeader) so a user who knows the app recognises the web.

export function Screen({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-ground text-parchment font-sans">
      <div
        className="pointer-events-none fixed inset-0"
        style={{ background: "radial-gradient(80% 60% at 50% 0%, rgba(42,16,18,.55), transparent 70%)" }}
      />
      <div className="relative mx-auto max-w-5xl px-5 pb-24 pt-6">{children}</div>
    </div>
  );
}

export function Header({ kicker, title }: { kicker?: string; title: string }) {
  return (
    <header className="mb-6">
      {kicker && (
        <div className="mb-1 font-mono text-[11px] uppercase tracking-[0.2em] text-gold">{kicker}</div>
      )}
      <h1 className="font-serif text-3xl text-ivory">{title}</h1>
    </header>
  );
}

export function GildedFrame({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl border border-gold/30 bg-cardInk p-4 ${className}`}>
      <div className="rounded-lg border border-gold/10 p-0">{children}</div>
    </div>
  );
}

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & { busy?: boolean; busyLabel?: string };

export function PrimaryButton({ children, busy, busyLabel, className = "", ...rest }: BtnProps) {
  return (
    <button
      {...rest}
      disabled={rest.disabled || busy}
      className={`w-full rounded-xl bg-goldGradient px-4 py-4 font-sans text-[15px] font-semibold text-buttonInk
        transition disabled:opacity-50 ${className}`}
    >
      {busy ? busyLabel || "Working…" : children}
    </button>
  );
}

export function QuietButton({ children, busy, className = "", ...rest }: BtnProps) {
  return (
    <button
      {...rest}
      disabled={rest.disabled || busy}
      className={`rounded-full border border-gold/25 bg-gold/[0.08] px-4 py-2.5 text-[13px] font-medium
        text-gold transition hover:border-gold/50 disabled:opacity-50 ${className}`}
    >
      {children}
    </button>
  );
}

export function Field(props: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`w-full rounded-lg border border-gold/35 bg-screenInk px-4 py-3.5 text-[16px] text-parchment
        outline-none placeholder:text-stoneDim focus:border-gold ${props.className || ""}`}
    />
  );
}

export function ErrorText({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return <p className="mt-2 text-[13px] text-reject">{children}</p>;
}

export function Muted({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <p className={`text-[13px] text-stoneDim ${className}`}>{children}</p>;
}

export function Spinner() {
  return (
    <div className="flex justify-center py-10">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-gold/30 border-t-gold" />
    </div>
  );
}

export function Empty({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <div className="rounded-xl border border-gold/20 bg-cardInk px-6 py-14 text-center">
      <h3 className="font-serif text-xl text-ivory">{title}</h3>
      <p className="mx-auto mt-2 max-w-sm text-[14px] leading-relaxed text-warmStone">{body}</p>
      {action && <div className="mt-5 flex justify-center">{action}</div>}
    </div>
  );
}
